// Shared logic for the two distinct sponsor groupings the admin sets:
//
//   • "Add filter"  (e.g. VIP) → the sponsor's FILTER tag, surfaced in
//        the "All categories" filter dropdown.
//   • "Sponsor type" (e.g. ABC) → the sponsor's TYPE, used to group
//        sponsors into named rows (lobby / event-info), like Top Sponsors.
//
// Both are admin categories distinguished by `categoryType`. In this
// platform the "Add filter" bucket (VIP) is `SPONSOR` (0); the
// "Sponsor type" bucket (ABC) is `SPONSOR_CATEGRORY` (3) / untyped and is
// used for grouping. Centralised here so the mapping is a one-line change
// if a deployment stores them the other way.

import { categoryType } from '@/constants/category-type-constants';

const FILTER_CATEGORY_TYPE = categoryType.SPONSOR; // 0 → "Add filter" (VIP)

function isFilterCat(c) {
  return c && c.categoryType === FILTER_CATEGORY_TYPE;
}

export default {
  methods: {
    // All categories in the store (read directly, not as a mapped
    // computed, so this mixin never collides with a host component that
    // already maps Categories getters).
    _spAllCats() {
      const raw = this.$store.getters['Categories/getItems'];
      return Array.isArray(raw) ? raw : Object.values(raw || {});
    },
    _spCatId(c) {
      if (c === null || c === undefined) return '';
      if (typeof c === 'string' || typeof c === 'number') return String(c);
      const keys = [
        'id',
        'Id',
        'ID',
        'categoryId',
        'CategoryId',
        'categoryID',
        'CategoryID',
        'cid',
        'Cid'
      ];
      for (const k of keys) {
        if (c[k] !== undefined && c[k] !== null && String(c[k]).trim() !== '') {
          return String(c[k]);
        }
      }
      return '';
    },
    _spCatLabel(c) {
      if (c === null || c === undefined) return '';
      if (typeof c === 'string' || typeof c === 'number') return String(c);
      const keys = [
        'label',
        'Label',
        'value',
        'Value',
        'name',
        'Name',
        'title',
        'Title',
        'caption',
        'Caption',
        'text',
        'Text',
        'categoryName',
        'CategoryName',
        'displayName',
        'DisplayName'
      ];
      for (const k of keys) {
        if (c[k] !== undefined && c[k] !== null && String(c[k]).trim() !== '') {
          return String(c[k]);
        }
      }
      return '';
    },
    // Raw category references on a sponsor — tolerant of the many field
    // names / shapes the API uses across versions.
    _spSponsorRefs(sponsor) {
      if (!sponsor) return [];
      const keys = [
        'sponsorCategories',
        'SponsorCategories',
        'sponsorCategoryIds',
        'SponsorCategoryIds',
        'categoryIds',
        'CategoryIds',
        'categories',
        'Categories',
        'category',
        'Category',
        'categoryId',
        'CategoryId'
      ];
      const out = [];
      for (const k of keys) {
        const v = sponsor[k];
        if (v === undefined || v === null) continue;
        (Array.isArray(v) ? v : [v]).forEach((x) => out.push(x));
      }
      return out;
    },
    // Resolve a sponsor's refs to full category objects (so we know each
    // one's categoryType). Refs may be ids, objects-with-categoryType, or
    // objects missing it; resolve by id, then by label, as fallbacks.
    _spResolveSponsorCats(sponsor) {
      const all = this._spAllCats();
      const byId = new Map();
      const byLabel = new Map();
      all.forEach((c) => {
        const id = String(this._spCatId(c));
        if (id) byId.set(id, c);
        const lbl = String(this._spCatLabel(c)).trim().toLowerCase();
        if (lbl) byLabel.set(lbl, c);
      });
      const out = [];
      this._spSponsorRefs(sponsor).forEach((ref) => {
        if (ref && typeof ref === 'object') {
          if (ref.categoryType !== undefined && ref.categoryType !== null) {
            out.push(ref);
            return;
          }
          const id = String(this._spCatId(ref));
          const lbl = String(this._spCatLabel(ref)).trim().toLowerCase();
          out.push(byId.get(id) || byLabel.get(lbl) || ref);
        } else if (ref !== null && ref !== undefined) {
          const key = String(ref);
          out.push(
            byId.get(key) ||
              byLabel.get(key.trim().toLowerCase()) || {
                id: key,
                label: key,
                categoryType: null
              }
          );
        }
      });
      return out;
    },
    sponsorFilterCategoriesOf(sponsor) {
      return this._spResolveSponsorCats(sponsor).filter((c) => isFilterCat(c));
    },
    sponsorTypeCategoriesOf(sponsor) {
      return this._spResolveSponsorCats(sponsor).filter((c) => !isFilterCat(c));
    },
    // Normalized label keys of a sponsor's FILTER categories (for the
    // dropdown's value matching).
    sponsorFilterKeys(sponsor) {
      return this.sponsorFilterCategoriesOf(sponsor)
        .map((c) => String(this._spCatLabel(c)).trim().toLowerCase())
        .filter(Boolean);
    },
    // Distinct { key, label } FILTER options actually used by the list —
    // these populate the "All categories" dropdown (e.g. just "VIP").
    sponsorFilterOptions(sponsors) {
      const seen = new Map();
      (sponsors || []).forEach((s) => {
        this.sponsorFilterCategoriesOf(s).forEach((c) => {
          const label = this._spCatLabel(c);
          const key = String(label).trim().toLowerCase();
          if (key && !seen.has(key)) seen.set(key, { key, label });
        });
      });
      return Array.from(seen.values());
    },
    sponsorFilterLabels(sponsor) {
      return this.sponsorFilterCategoriesOf(sponsor)
        .map((c) => this._spCatLabel(c))
        .filter(Boolean)
        .join(' · ');
    },
    // Group sponsors into one row per TYPE (named by the type), with any
    // sponsor that has no type collected into a trailing "Other" group.
    //
    // Row order is admin-driven:
    //   1. `typeOrder` (the order the admin arranged the sponsor types in
    //      the lobby builder) takes precedence,
    //   2. then the category's own `order` from the store,
    //   3. and the "Other" bucket is always last.
    sponsorTypeGroups(sponsors, typeOrder) {
      const list = Array.isArray(sponsors) ? sponsors : [];
      const order = new Map(); // typeKey → display order from the store
      this._spAllCats().forEach((c, i) => {
        if (!isFilterCat(c)) {
          order.set(String(this._spCatId(c)), parseInt(c && c.order) || i);
        }
      });
      const groups = new Map();
      const ungrouped = [];
      list.forEach((s) => {
        const types = this.sponsorTypeCategoriesOf(s);
        if (!types.length) {
          ungrouped.push(s);
          return;
        }
        types.forEach((c) => {
          const key = String(this._spCatId(c) || this._spCatLabel(c));
          if (!groups.has(key)) {
            groups.set(key, {
              id: key,
              label: this._spCatLabel(c) || 'Sponsors',
              order: order.has(key) ? order.get(key) : 999,
              sponsors: []
            });
          }
          groups.get(key).sponsors.push(s);
        });
      });

      // Admin arrangement → rank by position in `typeOrder` (match on id
      // or normalized label); -1 means "not explicitly arranged".
      const arranged = (Array.isArray(typeOrder) ? typeOrder : []).map(String);
      const arrangedLower = arranged.map((k) => k.trim().toLowerCase());
      const rankOf = (g) => {
        const byId = arranged.indexOf(String(g.id));
        if (byId !== -1) return byId;
        const byLabel = arrangedLower.indexOf(
          String(g.label).trim().toLowerCase()
        );
        return byLabel;
      };

      const out = Array.from(groups.values()).sort((a, b) => {
        const ra = rankOf(a);
        const rb = rankOf(b);
        if (ra !== -1 && rb !== -1) return ra - rb;
        if (ra !== -1) return -1; // admin-arranged types come first
        if (rb !== -1) return 1;
        return a.order - b.order; // fall back to the store's category order
      });
      if (ungrouped.length) {
        out.push({
          id: '__other__',
          label: 'Other',
          order: 9999,
          sponsors: ungrouped
        });
      }
      return out;
    },
    hasTypedSponsors(sponsors) {
      return (Array.isArray(sponsors) ? sponsors : []).some(
        (s) => this.sponsorTypeCategoriesOf(s).length > 0
      );
    },
    // Does this sponsor belong to the given category? `categoryRef` is
    // the admin's selected category (the lobby builder's "Select
    // category" value) — an id, a value, or a label. Empty ref → the
    // sponsor always matches (no filtering). Tolerant of how the
    // sponsor's category is stored (raw id/value, object, or a ref the
    // store resolves by id/label).
    sponsorInCategory(sponsor, categoryRef) {
      if (
        categoryRef === null ||
        categoryRef === undefined ||
        String(categoryRef).trim() === ''
      ) {
        return true;
      }
      const target = String(categoryRef).trim().toLowerCase();
      // Match against the sponsor's raw category references first.
      const raws = this._spSponsorRefs(sponsor);
      for (const r of raws) {
        if (r && typeof r === 'object') {
          if (String(this._spCatId(r)).trim().toLowerCase() === target) {
            return true;
          }
          if (String(this._spCatLabel(r)).trim().toLowerCase() === target) {
            return true;
          }
        } else if (r !== null && r !== undefined) {
          if (String(r).trim().toLowerCase() === target) return true;
        }
      }
      // Then against the categories resolved from the store (so a bare
      // id on the sponsor still matches a label-valued filter, etc.).
      return this._spResolveSponsorCats(sponsor).some(
        (c) =>
          String(this._spCatId(c)).trim().toLowerCase() === target ||
          String(this._spCatLabel(c)).trim().toLowerCase() === target
      );
    }
  }
};
